import React from "react";
import { useState, useEffect } from "react";
import { getCommentsByIds, createComment } from "../actions/index";

export interface PostInterface {
  id: string;
  content: string;
  userId: string;
  likedBy: string[];
  commentIds: string[];
  createdAt: Date;
  imageUrl?: string;
  videoUrl?: string;
}

export interface Comment {
  id: string;
  text: string;
  userId: string;
  postId: string;
  createdAt: Date;
}

interface PostProps {
  post: PostInterface;
  currentUserId: string;
  onLike: (postId: string) => Promise<void>;
  onUnlike: (postId: string) => Promise<void>;
}

const Post = ({ post, currentUserId, onLike, onUnlike }: PostProps) => {
  const [showComments, setShowComments] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [comments, setComments] = useState([] as Comment[]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const isLiked = post.likedBy.includes(currentUserId);

  // Fetch comments when modal is opened
  useEffect(() => {
    if (showComments && post.commentIds.length > 0) {
      const fetchComments = async () => {
        setIsLoadingComments(true);
        try {
          const fetchedComments = await getCommentsByIds(post.commentIds);
          setComments(fetchedComments);
        } catch (error) {
          console.error("Error fetching comments:", error);
        } finally {
          setIsLoadingComments(false);
        }
      };
      fetchComments();
    }
  }, [showComments, post.commentIds]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmittingComment) return;

    setIsSubmittingComment(true);
    try {
      await createComment(post.id, currentUserId, newComment.trim());
      setNewComment(""); // Clear input after successful submission

      // Refresh comments
      const updatedComments = await getCommentsByIds(post.commentIds);
      setComments(updatedComments);
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const postDate = new Date(date);
    const diffInHours = (now.getTime() - postDate.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      if (hours < 1) {
        const minutes = Math.floor(diffInHours * 60);
        return `${minutes}m`;
      }
      return `${hours}h`;
    } else {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[postDate.getMonth()];
      const day = postDate.getDate();
      return `${month} ${day}`;
    }
  };

  return (
    <article
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Modal for full-size image */}
      {showFullImage && post.imageUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            cursor: "pointer",
          }}
          onClick={() => setShowFullImage(false)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <img
              src={post.imageUrl}
              alt="Post"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </div>
        </div>
      )}

      {/* Modal for comments */}
      {showComments && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowComments(false)}
        >
          <div
            style={{
              position: "relative",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  color: "gray",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                }}
              >
                Comments ({post.commentIds.length})
              </h3>
              <button
                onClick={() => setShowComments(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Add comment form if not the post creator */}
            {post.userId !== currentUserId && (
              <form
                onSubmit={handleSubmitComment}
                style={{ marginBottom: "20px" }}
              >
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      borderRadius: "20px",
                      border: "1px solid #e5e7eb",
                      outline: "none",
                      fontSize: "0.875rem",
                    }}
                    disabled={isSubmittingComment}
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim() || isSubmittingComment}
                    style={{
                      padding: "8px 16px",
                      backgroundColor:
                        !newComment.trim() || isSubmittingComment
                          ? "#e5e7eb"
                          : "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "20px",
                      cursor:
                        !newComment.trim() || isSubmittingComment
                          ? "not-allowed"
                          : "pointer",
                      fontSize: "0.875rem",
                    }}
                  >
                    {isSubmittingComment ? "Posting..." : "Post"}
                  </button>
                </div>
              </form>
            )}

            {/* Comments list */}
            {isLoadingComments ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading comments...
              </div>
            ) : comments.length > 0 ? (
              comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    backgroundColor: "#f9fafb",
                    padding: "12px",
                    borderRadius: "8px",
                    marginBottom: "12px",
                    color: "#4b5563",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.8rem",
                        textAlign: "start",
                      }}
                    >
                      {comment.userId}
                    </p>
                    <p
                      style={{
                        fontSize: "0.8rem",
                        color: "gray",
                        textAlign: "start",
                      }}
                    >
                      {formatDate(comment.createdAt)}
                    </p>
                  </div>
                  <p
                    style={{
                      color: "#333",
                      marginTop: "8px",
                      textAlign: "start",
                    }}
                  >
                    {comment.text}
                  </p>
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#6b7280",
                }}
              >
                No comments yet
              </div>
            )}
          </div>
        </div>
      )}

      <div
        style={{
          padding: "24px",
        }}
      >
        {/* User info and timestamp */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "16px",
          }}
        >
          <p
            style={{
              fontSize: "0.8rem",
              textAlign: "start",
            }}
          >
            {post.userId}
          </p>
          <p
            style={{
              fontSize: "0.8rem",
              color: "gray",
              textAlign: "start",
            }}
          >
            {formatDate(post.createdAt)}
          </p>
        </div>

        {/* Post content */}
        <p
          style={{
            color: "#333",
            marginBottom: "16px",
            textAlign: "start",
          }}
        >
          {post.content}
        </p>

        {/* Post image */}
        {post.imageUrl && (
          <div
            style={{
              width: "100%",
              marginBottom: "16px",
              cursor: "pointer",
            }}
            onClick={() => setShowFullImage(true)}
          >
            <img
              src={post.imageUrl}
              alt="Post"
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </div>
        )}

        {/* Interactions */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "8px",
            }}
          >
            <button
              onClick={() => (isLiked ? onUnlike(post.id) : onLike(post.id))}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                border: "none",
                borderRadius: "20px",
                backgroundColor: isLiked ? "#fce7f3" : "#f3f4f6",
                color: isLiked ? "#db2777" : "#4b5563",
                cursor: "pointer",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill={isLiked ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span>{post.likedBy.length}</span>
            </button>

            <button
              onClick={() => setShowComments(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                border: "none",
                borderRadius: "20px",
                backgroundColor: "#f3f4f6",
                color: "#4b5563",
                cursor: "pointer",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              <span>{post.commentIds.length}</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default Post;
